<template>
  <div class="order-center-container">
    <div class="order-header">
      <h2>{{ $t('header.order_center') }}</h2>
    </div>
    
    <div class="order-content">
      <div v-if="loading" class="loading">
        {{ $t('loading') || '加载中...' }}
      </div>
      
      <div v-else-if="error" class="error">
        {{ error }}
      </div>
      
      <div v-else>
        <div v-if="orders.length === 0" class="no-orders">
          <p>{{ $t('no_orders') || '暂无订单信息' }}</p>
        </div>
        
        <div v-else class="orders-list">
          <div v-for="order in orders" :key="order.orderId" class="order-item">
            <div class="order-header">
              <span class="order-id">{{ $t('order_center.order_id') }}: {{ order.orderId }}</span>
              <span class="order-status" :class="order.status">{{ getOrderStatusText(order.status) }}</span>
            </div>
            <div class="order-details">
              <div class="order-info">
                <span class="label">{{ $t('order_center.product') }}:</span>
                <span class="value">{{ order.productId }}</span>
              </div>
              <div class="order-info">
                <span class="label">{{ $t('order_center.amount') }}:</span>
                <span class="value">¥{{ order.amount }}</span>
              </div>
              <div class="order-info">
                <span class="label">{{ $t('order_center.payment_method') }}:</span>
                <span class="value">{{ getPaymentMethodText(order.paymentMethod) }}</span>
              </div>
              <div class="order-info">
                <span class="label">{{ $t('order_center.created_at') }}:</span>
                <span class="value">{{ formatDate(order.createdAt) }}</span>
              </div>
              <div v-if="order.paidAt" class="order-info">
                <span class="label">{{ $t('order_center.paid_at') }}:</span>
                <span class="value">{{ formatDate(order.paidAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { encryptUserData, decryptUserData } from '../utils/web-crypto';

export default {
  name: 'OrderCenter',
  data() {
    return {
      orders: [],
      loading: true,
      error: null
    }
  },
  mounted() {
    this.loadOrders();
  },
  methods: {
    async loadOrders() {
      try {
        this.loading = true;
        this.error = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        // 准备请求数据
        const requestData = {
          action: 'getOrders'
        };
        
        // 加密请求数据
        const encryptedData = await encryptUserData(requestData);
        
        // 从后端API获取订单信息
        const response = await axios.post('/api/payment/get-orders', { 
          encryptedData: encryptedData 
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-id': 'web'
          }
        });
        
        if (response.data.success) {
          // 解密订单信息
          const decryptedData = await decryptUserData(response.data.encryptedData);
          this.orders = decryptedData.orders || [];
        } else {
          throw new Error(response.data.message || '获取订单信息失败');
        }
      } catch (error) {
        console.error('加载订单信息失败:', error);
        this.error = error.message || '加载订单信息失败';
      } finally {
        this.loading = false;
      }
    },
    getOrderStatusText(status) {
      const statusMap = {
        'pending': this.$t('status_pending') || '待支付',
        'paid': this.$t('status_paid') || '已支付',
        'failed': this.$t('status_failed') || '支付失败',
        'refunded': this.$t('status_refunded') || '已退款'
      };
      return statusMap[status] || status;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN');
    },
    getPaymentMethodText(paymentMethod) {
      if (paymentMethod === 'alipay') {
        return this.$t('order_center.alipay');
      }
      return paymentMethod;
    }
  }
}
</script>

<style scoped>
.order-center-container {
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.order-header h2 {
  margin: 0;
  color: #333;
}

.order-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading, .error, .no-orders {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.error {
  color: #dc3545;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.order-item {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
  background-color: #fafafa;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.order-id {
  font-weight: bold;
  color: #333;
}

.order-status {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.order-status.pending {
  background-color: #ffc107;
  color: #212529;
}

.order-status.paid {
  background-color: #28a745;
  color: white;
}

.order-status.failed {
  background-color: #dc3545;
  color: white;
}

.order-status.refunded {
  background-color: #6c757d;
  color: white;
}

.order-details {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.order-info {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.value {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

@media (max-width: 768px) {
  .order-center-container {
    padding: 10px;
  }
  
  .order-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .order-details {
    grid-template-columns: 1fr;
  }
}
</style>
